import { useState } from "react";
import {
  ClockCircleOutlined,
  CheckOutlined,
  RollbackOutlined,
  UpOutlined,
  DownOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";

const R = {
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  // Плътни акцентни фонове (primary бутонът) — еднакви в двете теми.
  brand: "#D97706",
  brandDeep: "#B45309",
};

interface MonitorFooterProps {
  onInfo?: () => void;
  onInProgress?: () => void;
  onComplete?: () => void;
  onReturn?: () => void;
  onNavigate?: (step: number) => void;
  canNavigate?: boolean;
  canInfo?: boolean;
  canComplete?: boolean;
  canInProgress?: boolean;
  canReturn?: boolean;
  selectedOrderKey?: string | null;
}

export const MonitorFooter = ({
  onInfo,
  onInProgress,
  onComplete,
  onReturn,
  onNavigate,
  canNavigate = false,
  canInfo = false,
  canComplete = true,
  canInProgress = true,
  canReturn = true,
  selectedOrderKey,
}: MonitorFooterProps) => {
  const { t } = useTranslation();
  const { isNarrow, isDesktop } = useViewport();
  const disabled = !selectedOrderKey;
  const navDisabled = !canNavigate;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        background: R.surface,
        borderTop: `1px solid ${R.border}`,
        padding: isNarrow ? "10px 12px" : "12px 20px",
        minHeight: isNarrow ? 64 : 72,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: isNarrow ? 6 : 10,
      }}
    >
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          display: "flex",
          alignItems: "center",
          gap: isNarrow ? 6 : 10,
        }}
      >
        <FooterBtn
          icon={<InfoCircleOutlined />}
          label={t("ordering.info")}
          onClick={onInfo}
          disabled={!canInfo}
        />

        <FooterBtn
          icon={<ClockCircleOutlined />}
          label={t("monitors.inProgress")}
          onClick={onInProgress}
          disabled={disabled || !canInProgress}
        />

        <FooterBtn
          icon={<CheckOutlined />}
          label={t("monitors.complete")}
          onClick={onComplete}
          disabled={disabled || !canComplete}
          variant="primary"
          shortcut="+"
        />

        <FooterBtn
          icon={<RollbackOutlined />}
          label={t("monitors.return")}
          onClick={onReturn}
          disabled={disabled || !canReturn}
          shortcut="−"
        />
      </div>

      <div
        style={{
          display: isDesktop ? "flex" : "none",
          alignItems: "center",
          gap: isNarrow ? 4 : 6,
          flexShrink: 0,
        }}
      >
        <NavBtn
          title={t("monitors.navUp", "Up")}
          onClick={() => onNavigate?.(-1)}
          disabled={navDisabled}
          direction="up"
        />
        <NavBtn
          title={t("monitors.navDown", "Down")}
          onClick={() => onNavigate?.(1)}
          disabled={navDisabled}
          direction="down"
        />
        <NavBtn
          title={t("monitors.navUp2", "Up 2")}
          onClick={() => onNavigate?.(-2)}
          disabled={navDisabled}
          double
          direction="up"
        />
        <NavBtn
          title={t("monitors.navDown2", "Down 2")}
          onClick={() => onNavigate?.(2)}
          disabled={navDisabled}
          double
          direction="down"
        />
      </div>
    </div>
  );
};

function NavBtn({
  onClick,
  disabled,
  title,
  direction,
  double,
}: {
  onClick: () => void;
  disabled: boolean;
  title?: string;
  direction: "up" | "down";
  double?: boolean;
}) {
  const { isNarrow } = useViewport();
  const [hover, setHover] = useState(false);
  const size = isNarrow ? 40 : 44;
  const Icon = direction === "up" ? UpOutlined : DownOutlined;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      onMouseEnter={() => !disabled && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        height: size,
        width: size,
        borderRadius: 10,
        background: disabled
          ? "transparent"
          : hover
            ? "var(--color-shade-1)"
            : "transparent",
        border: `1px solid ${disabled ? R.border : hover ? "var(--color-shade-3)" : R.border}`,
        color: disabled ? R.textMuted : R.textSoft,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        transition: "background 140ms ease, border-color 140ms ease, color 140ms ease",
        opacity: disabled ? 0.55 : 1,
        padding: 0,
        lineHeight: 1,
      }}
    >
      {double ? (
        <>
          <Icon style={{ fontSize: 11, marginBottom: -4 }} />
          <Icon style={{ fontSize: 11 }} />
        </>
      ) : (
        <Icon style={{ fontSize: 15 }} />
      )}
    </button>
  );
}

function FooterBtn({
  icon,
  label,
  onClick,
  disabled,
  variant = "ghost",
  extra,
  shortcut,
}: {
  icon?: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  variant?: "ghost" | "primary";
  extra?: React.ReactNode;
  shortcut?: string;
}) {
  const { isNarrow, isMobile } = useViewport();
  const [hover, setHover] = useState(false);
  const isPrimary = variant === "primary";
  const hideIcon = isMobile && !isPrimary;

  const bg = disabled
    ? isPrimary
      ? "rgba(217,119,6,0.35)"
      : "transparent"
    : isPrimary
      ? hover
        ? R.brandDeep
        : R.brand
      : hover
        ? "var(--color-shade-1)"
        : "transparent";

  const color = isPrimary
    ? "#FFFFFF"
    : disabled
      ? R.textMuted
      : R.textSoft;

  const borderColor = isPrimary
    ? "transparent"
    : disabled
      ? R.border
      : hover
        ? "var(--color-shade-3)"
        : R.border;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => !disabled && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        height: isNarrow ? 44 : 48,
        padding: isNarrow ? "0 14px" : "0 18px",
        borderRadius: 10,
        background: bg,
        border: `1px solid ${borderColor}`,
        color,
        fontFamily: "inherit",
        fontSize: isNarrow ? 13 : 13.5,
        fontWeight: 600,
        letterSpacing: -0.1,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: hideIcon ? 0 : 8,
        transition:
          "background 140ms ease, border-color 140ms ease, color 140ms ease",
        whiteSpace: "nowrap",
        flexShrink: 0,
        opacity: disabled && !isPrimary ? 0.55 : 1,
      }}
    >
      {icon && !hideIcon && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: isNarrow ? 15 : 16,
          }}
        >
          {icon}
        </span>
      )}
      <span>{label}</span>
      {extra}
      {shortcut && !isNarrow && (
        <span
          style={{
            position: "absolute",
            top: 4,
            right: 7,
            fontFamily: '"JetBrains Mono", ui-monospace, monospace',
            fontSize: 13,
            lineHeight: 1,
            fontWeight: 700,
            letterSpacing: 0,
            color: isPrimary ? "rgba(255,255,255,0.8)" : R.textMuted,
            pointerEvents: "none",
          }}
        >
          {shortcut}
        </span>
      )}
    </button>
  );
}
